import RadioGroup, { RadioGroupOption } from "@/components/radio-group"
import SingleReading from "@/components/single-reading"
import { FormulaInputSchema, FormulaSchema } from "@/lib/form/schema"
import { cn } from "@/lib/utils"
import { useEffect, useState } from "react"

export interface FormulaFormViewProps {
  className?: string
  schema?: FormulaSchema | null
  value?: number[]
  onChange?: (values: number[]) => void
}

export default function FormulaFormView({
  className,
  schema,
  value,
  onChange,
}: FormulaFormViewProps) {
  const [localValue, setLocalValue] = useState<Record<string, number>>({})
  const renderable = useRenderableSchema()
  const factorOptions = useFactorOptions()

  useInit()

  function useInit() {
    useEffect(() => {
      if (!schema?.inputs) {
        return
      }

      setLocalValue((prev) => {
        const newLocalValue = {
          ...prev,
        }

        for (const [index, input] of schema.inputs.entries()) {
          if (typeof value?.at(index) === "number") {
            newLocalValue[input.variable] = value?.at(index) || 0
          }
        }

        if (schema.factors?.DEFAULT) {
          newLocalValue.F = schema.factors?.DEFAULT || 0
        }

        return newLocalValue
      })
    }, [schema])
  }

  function formatLabel(inputSchema: FormulaInputSchema) {
    if (!inputSchema.unit) {
      return inputSchema.name
    }

    return `${inputSchema.name} (${inputSchema.unit})`
  }

  function useFactorOptions() {
    const [options, setOptions] = useState<RadioGroupOption[]>([])

    useEffect(() => {
      if (!schema?.factors || schema.factors.DEFAULT) {
        setOptions([])

        return
      }

      const newOptions = Object.entries(schema.factors).map(
        ([key, factor]) => ({
          label: key,
          value: `${factor}`,
        })
      )
      setOptions(newOptions)
    }, [schema])

    return options
  }

  function useRenderableSchema() {
    const [renderable, setRenderable] = useState<FormulaSchema | null>(null)

    useEffect(() => {
      if (!schema) {
        setRenderable(null)

        return
      }

      setRenderable({
        ...schema,
        inputs: schema.inputs.filter((input) => input.variable !== "F"),
      })
    }, [schema])

    return renderable
  }

  function handleReadingChange(field: string, value: string) {
    setLocalValue((prev) => {
      const newValue = {
        ...prev,
        [field]: Number(value),
      }

      if (onChange && schema?.inputs) {
        const emitted: number[] = []
        for (const input of schema.inputs) {
          emitted.push(newValue[input.variable])
        }

        onChange(emitted)
      }

      return newValue
    })
  }

  if (!renderable) {
    return false
  }

  return (
    <form className={cn("grid grid-cols-2 gap-x-2.5 gap-y-4", className)}>
      {renderable.inputs.map((input, index) => (
        <SingleReading
          key={input.id}
          className={cn(
            index === renderable.inputs.length - 1 && index % 2 === 0
              ? "col-span-2"
              : "col-span-1"
          )}
          type="text"
          inputMode="numeric"
          direction="column"
          label={formatLabel(input)}
          value={
            typeof localValue[input.variable] === "number"
              ? `${localValue[input.variable]}`
              : ""
          }
          onChange={(value) => handleReadingChange(input.variable, value)}
        />
      ))}

      {!!factorOptions.length && (
        <RadioGroup
          className="col-span-2"
          label="Select the factor"
          options={factorOptions}
          value={typeof localValue.F === "number" ? `${localValue.F}` : ""}
          onChange={(value) => handleReadingChange("F", value)}
        />
      )}

      {renderable.factors && (
        <div
          className="
            h-11.5 p-3 border border-dashed border-label-text mb-4 mt-4 bg-band-fill
            flex justify-between uppercase text-button-black text-sm font-medium col-span-2
          "
        >
          <h4>factor</h4>
          <p>{localValue.F || "--"}</p>
        </div>
      )}
    </form>
  )
}
